import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import { cn } from '@/lib/utils'
import { Product, Review, User } from '@/types/api'
import { Check, Loader2, Star } from 'lucide-react'
import React, { useEffect, useState } from 'react'

// Simple debounce impl since I don't want to check hooks folder
function useDebounceValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])
  return debouncedValue
}

interface ReviewFormProps {
  review?: Review
  onSuccess: () => void
  onCancel: () => void
}

export function ReviewForm({ review, onSuccess, onCancel }: ReviewFormProps) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    rating: review?.rating || 5,
    review: review?.review || '',
    is_verified: review?.is_verified || false,
    is_featured: review?.is_featured || false,
    // Date field
    created_at: review?.created_at ? review.created_at.split('T')[0] : '',
    // Fields for creation only
    product_id: review?.product_id || '',
    user_id: review?.user_id || '',
    order_id: review?.order_id || '',
  })

  // Search states
  const [productSearch, setProductSearch] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const debouncedProductSearch = useDebounceValue(productSearch, 500)
  const debouncedUserSearch = useDebounceValue(userSearch, 500)

  const [products, setProducts] = useState<Product[]>([])
  const [users, setUsers] = useState<User[]>([])

  const isEdit = !!review

  useEffect(() => {
    if (isEdit) return

    if (debouncedProductSearch) {
      apiClient
        .getProducts({ search: debouncedProductSearch, per_page: 5 })
        .then((res) => setProducts(res.data))
        .catch(() => {})
    }
  }, [debouncedProductSearch, isEdit])

  useEffect(() => {
    if (isEdit) return
    if (debouncedUserSearch) {
      apiClient
        .getUsers({ search: debouncedUserSearch, per_page: 5 })
        .then((res) => {
          setUsers(res.data)
        })
        .catch(() => {})
    }
  }, [debouncedUserSearch, isEdit])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (isEdit && review) {
        await apiClient.updateReview(review.id, {
          rating: formData.rating,
          review: formData.review,
          is_verified: formData.is_verified,
          is_featured: formData.is_featured,
          created_at: formData.created_at || undefined,
        })
        toast({
          title: 'Success',
          description: 'Review updated successfully',
        })
      } else {
        await apiClient.createReview({
          rating: formData.rating,
          review: formData.review,
          is_verified: formData.is_verified,
          is_featured: formData.is_featured,
          product_id: formData.product_id,
          user_id: formData.user_id,
          order_id: formData.order_id || undefined,
        })
        toast({
          title: 'Success',
          description: 'Review created successfully',
        })
      }
      onSuccess()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save review',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {!isEdit && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Search product..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              {products.length > 0 && productSearch && (
                <div className="border rounded-md max-h-40 overflow-y-auto bg-gray-50">
                  {products.map((p) => (
                    <div
                      key={p.id}
                      className={cn(
                        'p-2 text-sm cursor-pointer hover:bg-primary-50',
                        formData.product_id === p.id && 'bg-primary-100'
                      )}
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, product_id: p.id }))
                        setProductSearch(p.name)
                        setProducts([])
                      }}
                    >
                      {p.name}
                    </div>
                  ))}
                </div>
              )}
              {formData.product_id && (
                <div className="text-xs text-green-600">Product selected</div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Search user by name/email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              {users.length > 0 && userSearch && (
                <div className="border rounded-md max-h-40 overflow-y-auto bg-gray-50">
                  {users.map((u) => (
                    <div
                      key={u.id}
                      className={cn(
                        'p-2 text-sm cursor-pointer hover:bg-primary-50',
                        formData.user_id === u.id && 'bg-primary-100'
                      )}
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, user_id: u.id }))
                        setUserSearch(u.full_name + ' (' + u.email + ')')
                        setUsers([])
                      }}
                    >
                      {u.full_name} <span className="text-gray-500 text-xs">{u.email}</span>
                    </div>
                  ))}
                </div>
              )}
              {formData.user_id && <div className="text-xs text-green-600">User selected</div>}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order ID (Optional)
            </label>
            <input
              type="text"
              value={formData.order_id}
              onChange={(e) => setFormData((prev) => ({ ...prev, order_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter Order UUID if available"
            />
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={(e) => {
                e.preventDefault()
                setFormData((prev) => ({ ...prev, rating: star }))
              }}
              className={cn(
                'w-8 h-8 rounded-full transition-all flex items-center justify-center',
                formData.rating >= star
                  ? 'text-yellow-400 bg-yellow-50 hover:bg-yellow-100'
                  : 'text-gray-300 hover:bg-gray-100'
              )}
            >
              <Star
                className={cn(
                  'w-5 h-5 pointer-events-none',
                  formData.rating >= star && 'fill-current'
                )}
              />
            </button>
          ))}
          <span className="ml-2 font-medium text-gray-700">{formData.rating}/5</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Review</label>
        <textarea
          value={formData.review}
          onChange={(e) => setFormData((prev) => ({ ...prev, review: e.target.value }))}
          rows={5}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Write the review content..."
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Review Date</label>
        <input
          type="date"
          value={formData.created_at}
          onChange={(e) => setFormData((prev) => ({ ...prev, created_at: e.target.value }))}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mt-1">Leave empty to use the original date</p>
      </div>

      <div className="flex gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <div
            className={cn(
              'w-5 h-5 border rounded flex items-center justify-center transition-colors',
              formData.is_verified
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'border-gray-300 bg-white'
            )}
          >
            {formData.is_verified && <Check className="w-3 h-3" />}
            <input
              type="checkbox"
              className="hidden"
              checked={formData.is_verified}
              onChange={(e) => setFormData((prev) => ({ ...prev, is_verified: e.target.checked }))}
            />
          </div>
          <span className="text-sm text-gray-700">Verified Purchase</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <div
            className={cn(
              'w-5 h-5 border rounded flex items-center justify-center transition-colors',
              formData.is_featured
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'border-gray-300 bg-white'
            )}
          >
            {formData.is_featured && <Check className="w-3 h-3" />}
            <input
              type="checkbox"
              className="hidden"
              checked={formData.is_featured}
              onChange={(e) => setFormData((prev) => ({ ...prev, is_featured: e.target.checked }))}
            />
          </div>
          <span className="text-sm text-gray-700">Featured Review</span>
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {isEdit ? 'Update Review' : 'Create Review'}
        </button>
      </div>
    </form>
  )
}
